/**
 * CreateNotebookModal - Modal for creating new Ideas Kitchen
 *
 * @module CreateNotebookModal
 */

import React, { useState, useRef } from 'react';

import { X, FileUp, Plus, Trash2, FileText, Loader2, AlertTriangle } from 'lucide-react';

import ideasKitchenLogo from '../../../assets/ideas-kitchen-logo.png';

const CreateNotebookModal = ({ onClose, onCreate }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [files, setFiles] = useState([]);
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState(null);

  const fileInputRef = useRef(null);

  const handleFileSelect = e => {
    const selectedFiles = Array.from(e.target.files || []);
    setFiles(prev => [...prev, ...selectedFiles]);
    e.target.value = ''; // Reset input
  };

  const handleRemoveFile = index => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleDrop = e => {
    e.preventDefault();
    const droppedFiles = Array.from(e.dataTransfer.files || []);
    setFiles(prev => [...prev, ...droppedFiles]);
  };

  const handleCreate = async () => {
    if (!title.trim()) {
      setError('Please enter a title');
      return;
    }

    setIsCreating(true);
    setError(null);

    try {
      await onCreate(title, description, files);
    } catch (err) {
      setError(err.message || 'Failed to create kitchen');
    } finally {
      setIsCreating(false);
    }
  };

  const formatFileSize = bytes => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="relative w-full max-w-lg bg-white dark:bg-neutral-800 rounded-2xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-neutral-700">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 flex items-center justify-center">
              <img
                src={ideasKitchenLogo}
                alt="Ideas Kitchen"
                className="h-9 w-9 object-contain"
                draggable={false}
              />
            </div>
            <h2 className="text-lg font-semibold text-slate-800 dark:text-white">
              New Ideas Kitchen
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-100 dark:hover:bg-neutral-700 rounded-lg transition-colors"
          >
            <X className="h-5 w-5 text-slate-500 dark:text-neutral-400" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-5">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-neutral-300 mb-1.5">
              Kitchen Name *
            </label>
            <input
              type="text"
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder="e.g., Q4 Contract Analysis"
              className="w-full px-4 py-2.5 bg-slate-50 dark:bg-neutral-900 border border-slate-200 dark:border-neutral-700 rounded-xl text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
              autoFocus
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-neutral-300 mb-1.5">
              Description (optional)
            </label>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="What are you exploring?"
              rows={2}
              className="w-full px-4 py-2.5 bg-slate-50 dark:bg-neutral-900 border border-slate-200 dark:border-neutral-700 rounded-xl text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent resize-none transition-all"
            />
          </div>

          {/* File Upload Zone */}
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-neutral-300 mb-1.5">
              Initial Sources (optional)
            </label>
            <div
              onDrop={handleDrop}
              onDragOver={e => e.preventDefault()}
              className="border-2 border-dashed border-slate-200 dark:border-neutral-700 rounded-xl p-6 text-center hover:border-amber-400 dark:hover:border-amber-600 transition-colors cursor-pointer"
              onClick={() => fileInputRef.current?.click()}
            >
              <FileUp className="h-8 w-8 text-slate-400 dark:text-neutral-500 mx-auto mb-2" />
              <p className="text-sm text-slate-600 dark:text-neutral-400">
                Drop files here or{' '}
                <span className="text-amber-600 dark:text-amber-400">browse</span>
              </p>
              <p className="text-xs text-slate-400 dark:text-neutral-500 mt-1">
                PDF, DOCX, TXT, Excel, CSV, Images
              </p>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={handleFileSelect}
              className="hidden"
              accept=".pdf,.docx,.txt,.xlsx,.csv,.png,.jpg,.jpeg,.gif,.webp"
            />
          </div>

          {/* Selected Files */}
          {files.length > 0 && (
            <div className="space-y-2 max-h-40 overflow-y-auto">
              {files.map((file, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-2 bg-slate-50 dark:bg-neutral-900 rounded-lg"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <FileText className="h-4 w-4 text-slate-400 flex-shrink-0" />
                    <span className="text-sm text-slate-700 dark:text-neutral-300 truncate">
                      {file.name}
                    </span>
                    <span className="text-xs text-slate-400 dark:text-neutral-500 flex-shrink-0">
                      {formatFileSize(file.size)}
                    </span>
                  </div>
                  <button
                    onClick={() => handleRemoveFile(index)}
                    className="p-1 hover:bg-slate-200 dark:hover:bg-neutral-700 rounded"
                  >
                    <Trash2 className="h-4 w-4 text-slate-400" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Exploratory Note */}
          <div className="flex items-start gap-2 p-3 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900/50 rounded-xl">
            <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-amber-700 dark:text-amber-300">
              Ideas Kitchen outputs are exploratory and non-deterministic. For verified analysis,
              use Intelligence Studio.
            </p>
          </div>

          {/* Error */}
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900/50 rounded-xl">
              <AlertTriangle className="h-4 w-4 text-red-600 dark:text-red-400" />
              <p className="text-xs text-red-700 dark:text-red-300">{error}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-200 dark:border-neutral-700 bg-slate-50 dark:bg-neutral-900">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-slate-600 dark:text-neutral-400 hover:bg-slate-100 dark:hover:bg-neutral-800 rounded-lg transition-colors"
            disabled={isCreating}
          >
            Cancel
          </button>
          <button
            onClick={handleCreate}
            disabled={isCreating || !title.trim()}
            className="flex items-center gap-2 px-5 py-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-sm font-medium rounded-lg hover:from-amber-600 hover:to-orange-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md"
          >
            {isCreating ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Creating...
              </>
            ) : (
              <>
                <Plus className="h-4 w-4" />
                Create Kitchen
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateNotebookModal;
